import {
  ObjecttenancyRent,
  ObjecttenancyRentAdjustment,
  ObjecttenancyTenancy,
  TenancyTerminationType,
  TypeDate
} from '@/types/api'

/** Runtime values may include API sentinels (e.g. -1) despite the TypeDate type. */
type TenancyDateInput = TypeDate | null | undefined | number

/** Onboarding drafts carry `internalId` until first persist. */
type TenancyWithDraftId = ObjecttenancyTenancy & { internalId?: string | number }

export function typeDateToComparableInput(value: TenancyDateInput): string | null {
  if (value == null || value === -1) {
    return null
  }
  if (typeof value !== 'object' || Object.keys(value).length === 0) {
    return null
  }
  return value.year !== -1 && value.year > 0
    ? String(value.year).padStart(4, '0') + '-' + ('0' + value.month).slice(-2) + '-' + ('0' + value.day).slice(-2)
    : null
}

function endingAtToSortTimestamp(endingAt: TenancyDateInput): number {
  const dateInput = typeDateToComparableInput(endingAt)
  return dateInput ? new Date(dateInput).getTime() : 0
}

export function isActiveTenancyStatus(status: string | undefined): boolean {
  return status === 'TENANCY_STATUS_CURRENT' || status === 'TENANCY_STATUS_FUTURE'
}

/**
 * Pick the tenancy to use for termination flows: explicit id, then active without end date, else latest by endingAt.
 */
export function resolveLastTenancy(
  tenancies: ObjecttenancyTenancy[],
  tenancyId?: string | number | null
): ObjecttenancyTenancy | undefined {
  if (!tenancies.length) {
    return undefined
  }
  if (tenancyId) {
    const byId = tenancies.find(x => String(x.id) === String(tenancyId))
    if (byId) {
      return byId
    }
  }
  const activeWithoutEnding = tenancies.find(x => isActiveTenancyStatus(x.status) && !x.endingAt)
  if (activeWithoutEnding) {
    return activeWithoutEnding
  }
  return [...tenancies].sort((a, b) => endingAtToSortTimestamp(b.endingAt) - endingAtToSortTimestamp(a.endingAt))[0]
}

export function isMissingTenancyDate(value: TenancyDateInput): boolean {
  return value == null || value === -1 || !typeDateToComparableInput(value)
}

function addOneDayToTypeDate(value: TypeDate): TypeDate | null {
  const input = typeDateToComparableInput(value)
  if (!input) {
    return null
  }
  const date = new Date(input)
  date.setDate(date.getDate() + 1)
  return {
    year: date.getFullYear(),
    month: date.getMonth() + 1,
    day: date.getDate()
  }
}

function subtractOneDayFromTypeDate(value: TypeDate): TypeDate | null {
  const input = typeDateToComparableInput(value)
  if (!input) {
    return null
  }
  const date = new Date(input)
  date.setDate(date.getDate() - 1)
  return {
    year: date.getFullYear(),
    month: date.getMonth() + 1,
    day: date.getDate()
  }
}

function typeDatesEqual(a: TenancyDateInput, b: TenancyDateInput): boolean {
  const aInput = typeDateToComparableInput(a)
  const bInput = typeDateToComparableInput(b)
  return aInput != null && bInput != null && aInput === bInput
}

function getLatestRentAdjustment(tenancy: Pick<ObjecttenancyTenancy, 'rent'>): ObjecttenancyRentAdjustment | null {
  const adjustments = tenancy.rent?.adjustments
  if (!adjustments?.length) {
    return null
  }
  return [...adjustments].sort((a, b) => {
    const aMs = typeDateToMs(a.byDate as TenancyDateInput) || 0
    const bMs = typeDateToMs(b.byDate as TenancyDateInput) || 0
    return bMs - aMs
  })[0]
}

/** True when newStart is strictly after previous.endingAt + 1 day (gap between contracts). */
export function hasGapAfterPreviousTenancy(
  previous: ObjecttenancyTenancy,
  newStart: TenancyDateInput
): boolean {
  if (!previous || previous.vacancy) {
    return false
  }
  const newStartMs = typeDateToMs(newStart)
  const dayAfterPreviousEnd = addOneDayToTypeDate(previous.endingAt as TypeDate)
  const dayAfterMs = typeDateToMs(dayAfterPreviousEnd)
  if (newStartMs == null || dayAfterMs == null) {
    return false
  }
  return newStartMs > dayAfterMs
}

/** Copy additional-cost billing type from previous tenancy onto target rent (top-level + latest adjustment). */
export function applyAdditionalCostsBillingFromPrevious(
  targetRent: ObjecttenancyRent,
  previousTenancy: ObjecttenancyTenancy
): void {
  if (!targetRent || !previousTenancy?.rent) {
    return
  }
  targetRent.additionalCostsBillingType = previousTenancy.rent.additionalCostsBillingType
  const previousAdjustment = getLatestRentAdjustment(previousTenancy)
  const targetAdjustment = getLatestRentAdjustment({ rent: targetRent })
  if (previousAdjustment && targetAdjustment) {
    targetAdjustment.additionalCostsBillingType = previousAdjustment.additionalCostsBillingType
  }
}

export function gapVacancyBillingMatchesPrevious(
  vacancy: ObjecttenancyTenancy,
  previousTenancy: ObjecttenancyTenancy
): boolean {
  if (!vacancy?.rent || !previousTenancy?.rent) {
    return true
  }
  if (vacancy.rent.additionalCostsBillingType !== previousTenancy.rent.additionalCostsBillingType) {
    return false
  }
  const vacancyAdjustment = getLatestRentAdjustment(vacancy)
  const previousAdjustment = getLatestRentAdjustment(previousTenancy)
  if (!vacancyAdjustment || !previousAdjustment) {
    return vacancyAdjustment === previousAdjustment
  }
  return vacancyAdjustment.additionalCostsBillingType === previousAdjustment.additionalCostsBillingType
}

export function patchGapVacancyBillingFromPrevious(
  vacancy: ObjecttenancyTenancy,
  previousTenancy: ObjecttenancyTenancy
): ObjecttenancyTenancy {
  const patched = JSON.parse(JSON.stringify(vacancy)) as ObjecttenancyTenancy
  applyAdditionalCostsBillingFromPrevious(patched.rent, previousTenancy)
  return patched
}

/** Locate auto-created gap vacancy between previous contract end and new tenancy start. */
export function findGapVacancyBetween(
  tenancies: ObjecttenancyTenancy[],
  previous: ObjecttenancyTenancy,
  newStart: TenancyDateInput
): ObjecttenancyTenancy | null {
  if (!previous?.endingAt) {
    return null
  }
  const expectedStart = addOneDayToTypeDate(previous.endingAt as TypeDate)
  const expectedEnd = subtractOneDayFromTypeDate(newStart as TypeDate)
  if (!expectedStart) {
    return null
  }

  return tenancies.find(t => {
    if (!t.vacancy || t.objectId !== previous.objectId) {
      return false
    }
    if (!typeDatesEqual(t.startingAt, expectedStart)) {
      return false
    }
    if (expectedEnd && !isMissingTenancyDate(t.endingAt as TenancyDateInput)) {
      return typeDatesEqual(t.endingAt, expectedEnd)
    }
    return true
  }) || null
}

/** endingAt + 1 day as TypeDate (for nextPossibleMoveInAt). */
export function nextPossibleMoveInFromEndingAt(endingAt: TypeDate): TypeDate | null {
  return addOneDayToTypeDate(endingAt)
}

/**
 * Resolve the previous non-vacancy tenancy from the add-tenancy `lastTenancies` list
 * (sorted newest-first; index 0 may be a vacancy with the real previous at index 1).
 */
export function resolvePreviousFromLastTenancies(lastTenancies: ObjecttenancyTenancy[]): ObjecttenancyTenancy | null {
  if (!lastTenancies?.length) {
    return null
  }
  if (lastTenancies[0].vacancy === false) {
    return lastTenancies[0]
  }
  if (lastTenancies.length > 1 && lastTenancies[1].vacancy === false) {
    return lastTenancies[1]
  }
  return null
}

function typeDateToMs(value: TenancyDateInput): number | null {
  const input = typeDateToComparableInput(value)
  return input ? new Date(input).getTime() : null
}

/**
 * True when the new tenancy overlaps the previous contract such that the Attention modal
 * may need to shorten the previous tenancy. Returns false for a bounded historical tenancy
 * that ends before the previous contract start (COOZ-15913).
 */
export function newTenancyRequiresShorteningPrevious(
  newStart: TenancyDateInput,
  newEnd: TenancyDateInput,
  previousStart: TenancyDateInput,
  previousEnd: TenancyDateInput
): boolean {
  const newStartMs = typeDateToMs(newStart)
  const previousStartMs = typeDateToMs(previousStart)
  if (newStartMs == null || previousStartMs == null) {
    return false
  }

  const newEndMs = typeDateToMs(newEnd)
  const previousEndMs = typeDateToMs(previousEnd)

  if (newStartMs < previousStartMs) {
    if (newEndMs != null && newEndMs < previousStartMs) {
      return false
    }
    return true
  }

  if (previousEndMs != null) {
    return previousEndMs >= newStartMs
  }
  return newStartMs <= previousStartMs
}

/** True when new start is before the previous start but no end date is set (COOZ-15913). */
export function isHistoricalTenancyMissingEndDate(
  newStart: TenancyDateInput,
  newEnd: TenancyDateInput,
  previousStart: TenancyDateInput
): boolean {
  const newStartMs = typeDateToMs(newStart)
  const previousStartMs = typeDateToMs(previousStart)
  if (newStartMs == null || previousStartMs == null || newStartMs >= previousStartMs) {
    return false
  }
  return isMissingTenancyDate(newEnd)
}

export function isBoundedHistoricalTenancy(
  newStart: TenancyDateInput,
  newEnd: TenancyDateInput,
  previousStart: TenancyDateInput
): boolean {
  const newStartMs = typeDateToMs(newStart)
  const previousStartMs = typeDateToMs(previousStart)
  const newEndMs = typeDateToMs(newEnd)
  if (newStartMs == null || previousStartMs == null || newEndMs == null) {
    return false
  }
  return newStartMs < previousStartMs && newEndMs < previousStartMs
}

/**
 * Latest persisted non-vacancy tenancy on the same object, excluding a draft being created.
 */
export function findLatestPreviousNonVacancyTenancy(
  tenancies: TenancyWithDraftId[],
  objectId: string,
  exclude?: { id?: string; internalId?: string | number }
): ObjecttenancyTenancy | null {
  const list = tenancies
    .filter(t => {
      if (t.objectId !== objectId || t.vacancy) {
        return false
      }
      if (exclude?.id && t.id === exclude.id) {
        return false
      }
      if (exclude?.internalId != null && t.internalId === exclude.internalId) {
        return false
      }
      // Prefer existing (persisted) tenancies as "previous"
      if (!t.id || t.internalId) {
        return false
      }
      return true
    })
    .sort((a, b) => {
      const aIn = typeDateToComparableInput(a.startingAt as TenancyDateInput) || ''
      const bIn = typeDateToComparableInput(b.startingAt as TenancyDateInput) || ''
      return new Date(bIn).getTime() - new Date(aIn).getTime()
    })
  return list[0] || null
}

/**
 * COOZ-15689: Skip auto liability / move-in sync when Debit Position Until is set and the
 * off-term end is before that date (avoids locking the previous tenancy after setTermination).
 */
export function shouldSkipOffTermLiabilitySync(
  debitUntil: TenancyDateInput,
  offTermEnd: TenancyDateInput
): boolean {
  const debitInput = typeDateToComparableInput(debitUntil)
  const endInput = typeDateToComparableInput(offTermEnd)
  if (!debitInput || !endInput) {
    return false
  }
  return new Date(endInput).getTime() < new Date(debitInput).getTime()
}

/**
 * After off-term termination, liabilityEndsAt / movedOutAt / nextPossibleMoveInAt can stay on the
 * original contract end while endingAt was shortened. Align them to endingAt / endingAt+1 when stale.
 * Returns true when the tenancy was mutated and needs persisting.
 * COOZ-15689
 */
export function syncPreviousOffTermDates(
  tenancy: ObjecttenancyTenancy,
  debitUntil?: TenancyDateInput
): boolean {
  if (!tenancy || tenancy.vacancy || tenancy.terminationType !== TenancyTerminationType.TERMINATION_TYPE_OFF_TERM) {
    return false
  }
  const endingInput = typeDateToComparableInput(tenancy.endingAt as TenancyDateInput)
  if (!endingInput) {
    return false
  }
  if (shouldSkipOffTermLiabilitySync(debitUntil, tenancy.endingAt as TenancyDateInput)) {
    return false
  }
  const endingMs = new Date(endingInput).getTime()
  const expectedMoveIn = addOneDayToTypeDate(tenancy.endingAt as TypeDate)
  const expectedMoveInInput = typeDateToComparableInput(expectedMoveIn)
  if (!expectedMoveIn || !expectedMoveInInput) {
    return false
  }
  const expectedMoveInMs = new Date(expectedMoveInInput).getTime()

  const liabilityInput = typeDateToComparableInput(tenancy.liabilityEndsAt as TenancyDateInput)
  const movedOutInput = typeDateToComparableInput(tenancy.movedOutAt as TenancyDateInput)
  const moveInInput = typeDateToComparableInput(tenancy.nextPossibleMoveInAt as TenancyDateInput)

  const liabilityStale = isMissingTenancyDate(tenancy.liabilityEndsAt as TenancyDateInput) ||
    (liabilityInput != null && new Date(liabilityInput).getTime() > endingMs)
  const movedOutStale = isMissingTenancyDate(tenancy.movedOutAt as TenancyDateInput) ||
    (movedOutInput != null && new Date(movedOutInput).getTime() > endingMs)
  const moveInStale = isMissingTenancyDate(tenancy.nextPossibleMoveInAt as TenancyDateInput) ||
    (moveInInput != null && new Date(moveInInput).getTime() > expectedMoveInMs)

  if (!liabilityStale && !movedOutStale && !moveInStale) {
    return false
  }

  tenancy.liabilityEndsAt = tenancy.endingAt
  tenancy.movedOutAt = tenancy.endingAt
  tenancy.nextPossibleMoveInAt = expectedMoveIn
  return true
}

/**
 * Payload for ObjectApi.setTermination after syncPreviousOffTermDates (date-only; avoids full updateTenancy / COOZ-15245).
 */
export function buildSetTerminationPayloadFromTenancy(tenancy: ObjecttenancyTenancy) {
  return {
    terminatedOn: tenancy.terminatedOn || null,
    // ObjecttenancyTenancy has no terminateAt; after setTermination, endingAt is the contract end
    terminateAt: tenancy.endingAt || null,
    liabilityEndsAt: tenancy.liabilityEndsAt,
    nextPossibleMoveInAt: tenancy.nextPossibleMoveInAt || null,
    movedOutAt: tenancy.movedOutAt,
    terminationCreatedAt: tenancy.terminationCreatedAt,
    terminatedBy: tenancy.terminatedBy,
    terminationReason: tenancy.terminationReason,
    terminationType: tenancy.terminationType,
    terminationJustification: tenancy.terminationJustification,
    // Do not default to [] — that can wipe existing termination documents on sync.
    documentIds: tenancy.documentIds,
    terminationSentByTenantAt: tenancy.terminationSentByTenantAt
  }
}

/**
 * Clone + sync off-term dates; returns null if no API call needed.
 * Pass object.debitUntil so sync is skipped when off-term end is before Debit Position Until.
 */
export function prepareSyncedOffTermTermination(
  tenancy: ObjecttenancyTenancy,
  debitUntil?: TenancyDateInput
): { tenancy: ObjecttenancyTenancy; payload: ReturnType<typeof buildSetTerminationPayloadFromTenancy> } | null {
  const clone = JSON.parse(JSON.stringify(tenancy)) as ObjecttenancyTenancy
  if (!syncPreviousOffTermDates(clone, debitUntil)) {
    return null
  }
  return {
    tenancy: clone,
    payload: buildSetTerminationPayloadFromTenancy(clone)
  }
}

/**
 * When a tenancy contract end is shortened, align sub-tenant end dates so none end after the main contract.
 * Used before updateTenancy in flows that adjust the previous tenancy end (Add tenancy modal, mass rental).
 */
export function syncSubTenantEndingDates(tenancy: ObjecttenancyTenancy): void {
  if (!tenancy?.subTenants?.length || !tenancy.endingAt) {
    return
  }
  const tenancyEndInput = typeDateToComparableInput(tenancy.endingAt)
  if (!tenancyEndInput) {
    return
  }
  const tenancyEndMs = new Date(tenancyEndInput).getTime()
  for (let index = 0; index < tenancy.subTenants.length; index++) {
    const subTenant = tenancy.subTenants[index]
    const subEndInput = typeDateToComparableInput(subTenant.endingAt as TenancyDateInput)
    if (isMissingTenancyDate(subTenant.endingAt as TenancyDateInput)) {
      subTenant.endingAt = tenancy.endingAt
    } else if (subEndInput && new Date(subEndInput).getTime() > tenancyEndMs) {
      subTenant.endingAt = tenancy.endingAt
    }
  }
}
